import React, { createContext, useState, useCallback, useContext, ReactNode, useEffect } from 'react';
import { Room, getRooms, getRoom, createRoom, updateRoom, deleteRoom, parseToLanguages, RoomQueryParams, getNextSchedule, NextScheduleData } from '../services/roomService';
import { useNavigate } from 'react-router-dom';

interface RoomContextProps {
    rooms: Room[];
    currentRoom: Room | null;
    loading: boolean;
    error: string | null;
    totalRooms: number;
    currentPage: number;
    totalPages: number;
    fetchRooms: (params?: RoomQueryParams) => Promise<void>;
    fetchRoomById: (id: number) => Promise<void>;
    addRoom: (roomData: Partial<Room>) => Promise<Room>;
    editRoom: (id: number, roomData: Partial<Room>) => Promise<Room>;
    removeRoom: (id: number) => Promise<void>;
    setCurrentRoom: (room: Room | null) => void;
    clearError: () => void;
    fetchNextSchedule: (city: string, country?: string) => Promise<NextScheduleData>;
}

const RoomContext = createContext<RoomContextProps | undefined>(undefined);

export const useRooms = () => {
    const context = useContext(RoomContext);
    if (!context) {
        throw new Error('useRooms must be used within a RoomProvider');
    }
    return context;
};

interface RoomProviderProps {
    children: ReactNode;
}

export const RoomProvider: React.FC<RoomProviderProps> = ({ children }) => {
    const [rooms, setRooms] = useState<Room[]>([]);
    const [currentRoom, setCurrentRoom] = useState<Room | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [totalRooms, setTotalRooms] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);

    const navigate = useNavigate();
    const fetchRooms = useCallback(async (params?: RoomQueryParams) => {
        try {
            setLoading(true);
            setError(null);

            // Check if user is authenticated
            const token = localStorage.getItem('token');
            if (!token) {
                console.log("No authentication token found, redirecting to login");
                navigate("/login");
                return;
            }

            // Apply sensible defaults when params are not provided
            const queryParams: RoomQueryParams = {
                page: params?.page ?? 1,
                limit: params?.limit ?? 100,
                sort: params?.sort,
                order: params?.order,
            };

            const response = await getRooms(queryParams);

            // Parse toLanguages for each room
            const processedRooms = response.rooms.map(parseToLanguages);

            setRooms(processedRooms);
            setTotalRooms(response.total);
            setCurrentPage(response.currentPage);
            setTotalPages(response.pages);
        } catch (err: any) {
            // Handle authentication errors
            if (err?.response?.status === 401) {
                console.log("Authentication error (401), redirecting to login");
                localStorage.removeItem('token'); // Clear invalid token
                navigate("/login");
                return;
            }

            // Handle network errors
            if (!err.response && err.request) {
                setError('Network error. Please check your connection and try again.');
                return;
            }

            // Handle other API errors
            const errorMessage =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                'Failed to fetch rooms';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    const fetchRoomById = useCallback(async (id: number) => {
        try {
            setLoading(true);
            setError(null);

            // Check if user is authenticated
            const token = localStorage.getItem('token');
            if (!token) {
                console.log("No authentication token found, redirecting to login");
                navigate("/login");
                return;
            }

            const roomData = await getRoom(id);

            if (!roomData) {
                setError(`Room with ID ${id} not found`);
                console.error(`Room with ID ${id} not found`);
                return;
            }

            const processedRoom = parseToLanguages(roomData);
            setCurrentRoom(processedRoom);
        } catch (err: any) {
            console.error(`Error fetching room with ID ${id}:`, err);

            // Handle authentication errors
            if (err?.response?.status === 401) {
                console.log("Authentication error (401), redirecting to login");
                localStorage.removeItem('token'); // Clear invalid token
                navigate("/login");
                return;
            }

            // Handle not found errors
            if (err?.response?.status === 404) {
                setError(`Room with ID ${id} not found`);
                return;
            }

            // Handle network errors
            if (!err.response && err.request) {
                setError('Network error. Please check your connection and try again.');
                return;
            }

            // Handle other API errors
            const errorMessage =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                `Failed to fetch room with ID ${id}`;
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    const addRoom = useCallback(async (roomData: Partial<Room>) => {
        try {
            setLoading(true);
            setError(null);
            const newRoom = await createRoom(roomData);
            await fetchRooms(); // Refresh the room list
            return newRoom;
        } catch (err: any) {
            // Handle token expiration
            if (err?.response?.status === 401) {
                // Token expired, redirect to login
                navigate("/login");
                throw new Error("Your session has expired. Please log in again.");
            }

            // Handle other API errors
            const errorMessage =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                'Failed to create room';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [fetchRooms, navigate]);

    const editRoom = useCallback(async (id: number, roomData: Partial<Room>) => {
        try {
            setLoading(true);
            setError(null);
            const updatedRoom = await updateRoom(id, roomData);

            // Update rooms list if the room exists there
            setRooms(prevRooms =>
                prevRooms.map(room => room.id === id ? parseToLanguages(updatedRoom) : room)
            );

            // Update current room if it's the one being edited
            if (currentRoom && currentRoom.id === id) {
                setCurrentRoom(parseToLanguages(updatedRoom));
            }

            return updatedRoom;
        } catch (err: any) {
            const errorMessage =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                `Failed to update room with ID ${id}`;
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [currentRoom]);

    const removeRoom = useCallback(async (id: number) => {
        try {
            setLoading(true);
            setError(null);
            await deleteRoom(id);

            // Remove from rooms list
            setRooms(prevRooms => prevRooms.filter(room => room.id !== id));

            // Clear current room if it was deleted
            if (currentRoom && currentRoom.id === id) {
                setCurrentRoom(null);
            }
        } catch (err: any) {
            const errorMessage =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                `Failed to delete room with ID ${id}`;
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [currentRoom]);

    const clearError = () => setError(null);

    const fetchNextSchedule = useCallback(async (city: string, country?: string) => {
        // Authentication handled by axios interceptor; propagate errors
        return getNextSchedule(city, country);
    }, []);

    useEffect(() => {
        fetchRooms();
    }, [fetchRooms]);

    const value = {
        rooms,
        currentRoom,
        loading,
        error,
        totalRooms,
        currentPage,
        totalPages,
        fetchRooms,
        fetchRoomById,
        addRoom,
        editRoom,
        removeRoom,
        setCurrentRoom,
        clearError,
        fetchNextSchedule
    };

    return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
};
